import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SpeakerHigh, SpeakerX } from '@phosphor-icons/react';
import { ArrowClockwise } from 'phosphor-react';
import { Card } from '../../../components/shared/Card';
import { Text } from '../../../components/shared/Text';
import { Affirmation, createAffirmation } from '../../../models/Affirmation';
import LocalStorage from '../../../services/storage/LocalStorage';
import { getTodayString } from '../../../utils/formatters';

/**
 * Affirmation Card Component
 * Handles the affirmation reveal logic and display
 */
export const AffirmationCard: React.FC<{ fillHeight?: boolean }> = ({ fillHeight = false }) => {
  const [affirmation, setAffirmation] = useState<Affirmation | null>(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechSynthesis, setSpeechSynthesis] = useState<SpeechSynthesis | null>(null);

  useEffect(() => {
    loadAffirmation();
    // Initialize speech synthesis
    if ('speechSynthesis' in window) {
      setSpeechSynthesis(window.speechSynthesis);
    }
    
    // Cleanup: cancel any ongoing speech when component unmounts
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const loadAffirmation = () => {
    const today = getTodayString();
    const storageKey = `${LocalStorage.getKeys().AFFIRMATIONS}:${today}`;
    
    const stored = LocalStorage.getItem<Affirmation>(storageKey);
    
    // Check if stored affirmation uses old format (multiple intentions with "intentions:")
    const isOldFormat = stored && stored.text.includes('intentions:');
    
    if (stored && !isOldFormat) {
      // Affirmation already exists for today - use it (locked for the day)
      if (!stored.highlightedWords) {
        const baseWords = ['aligned', 'intention', 'journey', 'growth', 'path'];
        const textWords = stored.text.toLowerCase().match(/\b\w+\b/g) || [];
        const meaningfulWords = textWords.filter(word => 
          word.length > 2 && 
          !['the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'her', 'was', 'one', 'our', 'out', 'day', 'get', 'has', 'him', 'his', 'how', 'its', 'may', 'new', 'now', 'old', 'see', 'two', 'way', 'who', 'with', 'that', 'this', 'from', 'they', 'have', 'been', 'were', 'said', 'each', 'which', 'their', 'time', 'will', 'about', 'if', 'up', 'out', 'many', 'then', 'them', 'these', 'so', 'some', 'would', 'make', 'like', 'into', 'has', 'more', 'very', 'what', 'know', 'just', 'first', 'over', 'think', 'also', 'back', 'after', 'use', 'two', 'how', 'our', 'work', 'life', 'only', 'can', 'come', 'its', 'now', 'find', 'long', 'down', 'day', 'did', 'get', 'come', 'made', 'may', 'part'].includes(word)
        );
        stored.highlightedWords = [...baseWords, ...meaningfulWords.slice(0, 5)];
        LocalStorage.setItem(storageKey, stored);
      }
      setAffirmation(stored);
      const revealedKey = `${storageKey}:revealed`;
      const wasRevealed = LocalStorage.getItem<boolean>(revealedKey);
      setIsRevealed(wasRevealed || false);
    } else {
      // No affirmation for today OR old format detected - generate new one and lock it for the day
      // Clear old affirmation if it exists
      if (isOldFormat) {
        LocalStorage.removeItem(storageKey);
        const revealedKey = `${storageKey}:revealed`;
        LocalStorage.removeItem(revealedKey);
        const selectedIntentionKey = `${storageKey}:selectedIntention`;
        LocalStorage.removeItem(selectedIntentionKey);
      }
      setAffirmation(null);
      setIsRevealed(false);
      generateAffirmation(today, storageKey);
    }
  };

  const handleReset = () => {
    const today = getTodayString();
    const storageKey = `${LocalStorage.getKeys().AFFIRMATIONS}:${today}`;
    const revealedKey = `${storageKey}:revealed`;
    const selectedIntentionKey = `${storageKey}:selectedIntention`;
    
    // Clear all stored data
    LocalStorage.removeItem(storageKey);
    LocalStorage.removeItem(revealedKey);
    LocalStorage.removeItem(selectedIntentionKey);
    
    // Reset state
    setAffirmation(null);
    setIsRevealed(false);
    setIsGenerating(false);
    setIsSpeaking(false);
    
    // Cancel any ongoing speech
    if (speechSynthesis) {
      speechSynthesis.cancel();
    }
    
    // Reload affirmation
    loadAffirmation();
  };

  const handleTapToReveal = () => {
    if (isRevealed || isGenerating) return;
    
    const today = getTodayString();
    const storageKey = `${LocalStorage.getKeys().AFFIRMATIONS}:${today}`;
    
    // Always check for stored affirmation first (it's locked for the day)
    const stored = LocalStorage.getItem<Affirmation>(storageKey);
    if (stored) {
      // Affirmation exists - just reveal it (same one for the day)
      setAffirmation(stored);
      setIsRevealed(true);
      const revealedKey = `${storageKey}:revealed`;
      LocalStorage.setItem(revealedKey, true);
      return;
    }
    
    // If no stored affirmation, generate it (shouldn't happen if loadAffirmation worked, but as fallback)
    setIsGenerating(true);
    setIsRevealed(true);
    generateAffirmation(today, storageKey);
  };

  const generateAffirmation = (today: string, storageKey: string) => {
    const intentions = LocalStorage.getItem<Array<{ id: string; text: string }>>(
      LocalStorage.getKeys().INTENTIONS
    ) || [];

    if (intentions.length === 0) {
      setIsGenerating(false);
      return;
    }

    // Check if an intention has already been selected for today
    const selectedIntentionKey = `${storageKey}:selectedIntention`;
    let selectedIntention = LocalStorage.getItem<{ id: string; text: string }>(selectedIntentionKey);
    
    // If no intention selected for today, randomly select one
    if (!selectedIntention) {
      const randomIndex = Math.floor(Math.random() * intentions.length);
      selectedIntention = intentions[randomIndex];
      // Store the selected intention for the day
      LocalStorage.setItem(selectedIntentionKey, selectedIntention);
    }

    setTimeout(() => {
      const affirmationText = `Today, I am aligned with my intention: ${selectedIntention.text}. I trust in my journey and embrace the growth that comes with staying true to my path.`;

      const highlightedWords = ['aligned', 'intention', 'journey', 'growth', 'path'];
      
      // Only process the selected intention for highlighted words
      const words = selectedIntention.text.toLowerCase().split(/\s+/);
      words.forEach((word) => {
        const cleanWord = word.replace(/[.,!?;:]/g, '');
        if (cleanWord.length > 2 && !['the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'her', 'was', 'one', 'our', 'out', 'day', 'get', 'has', 'him', 'his', 'how', 'its', 'may', 'new', 'now', 'old', 'see', 'two', 'way', 'who', 'boy', 'did', 'she', 'use', 'her', 'many', 'than', 'them', 'these', 'so', 'some', 'would', 'make', 'like', 'into', 'time', 'has', 'look', 'more', 'write', 'go', 'see', 'number', 'no', 'way', 'could', 'people', 'my', 'than', 'first', 'water', 'been', 'call', 'who', 'oil', 'its', 'now', 'find', 'long', 'down', 'day', 'did', 'get', 'come', 'made', 'may', 'part'].includes(cleanWord)) {
          if (!highlightedWords.includes(cleanWord)) {
            highlightedWords.push(cleanWord);
          }
        }
      });

      // Only use the selected intention ID
      const relatedIntentionIds = [selectedIntention.id];
      const newAffirmation = createAffirmation(
        affirmationText,
        relatedIntentionIds,
        today
      );
      
      newAffirmation.highlightedWords = highlightedWords;
      // Store the affirmation - this locks it for the day (cannot be changed)
      LocalStorage.setItem(storageKey, newAffirmation);
      setAffirmation(newAffirmation);
      
      // Only set revealed if user has tapped to reveal
      // If generated on load, it won't be revealed until user taps
      if (isRevealed) {
        const revealedKey = `${storageKey}:revealed`;
        LocalStorage.setItem(revealedKey, true);
      }
      
      setIsGenerating(false);
    }, 1500);
  };

  const renderAffirmationText = (text: string, highlightedWords?: string[]) => {
    if (!highlightedWords || highlightedWords.length === 0) {
      return <>{text}</>;
    }

    const parts = text.split(/(\s+|[.,!?;:])/);
    
    return (
      <>
        {parts.map((part, index) => {
          if (!part.trim() && part !== ' ') {
            return <span key={index}>{part}</span>;
          }
          
          const cleanPart = part.replace(/[.,!?;:]/g, '').toLowerCase().trim();
          const shouldHighlight = cleanPart && highlightedWords.some(
            (hw) => hw.toLowerCase() === cleanPart
          );

          if (shouldHighlight) {
            return (
              <span
                key={index}
                style={{
                  fontFamily: 'Didot, serif',
                  fontStyle: 'italic',
                  fontWeight: 400,
                }}
              >
                {part}
              </span>
            );
          }
          return <span key={index}>{part}</span>;
        })}
      </>
    );
  };

  const cardStyle = fillHeight 
    ? { 
        minHeight: '60vh',
        height: 'calc(100vh - 50px - 109px - 24px - 106px)'
      }
    : { minHeight: '60vh' };

  return (
    <AnimatePresence mode="wait">
      {!isRevealed ? (
        <motion.div
          key="tap-to-reveal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={fillHeight ? 'h-full' : ''}
          style={fillHeight ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}}
        >
          <Card
            variant="elevated"
            className="cursor-pointer h-full flex flex-col justify-between"
            style={{
              ...cardStyle,
              ...(fillHeight ? { height: '100%' } : {}),
            }}
            onClick={handleTapToReveal}
            backgroundImage="/images/affirmation/dailyaffirmation.png"
          >
            {/* Text - centered */}
            <div className="relative z-10 flex flex-col items-center gap-6" style={{ flex: 1, justifyContent: 'center' }}>
              <div
                style={{
                  flexShrink: 0,
                  textAlign: 'center',
                  height: 'auto',
                  fontFamily: 'Didot, serif',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  fontSize: '24px',
                  lineHeight: '1.5',
                  display: 'block',
                  textTransform: 'uppercase',
                  color: '#FFFFFF',
                }}
              >
                TODAY'S<br />
                <span style={{ fontStyle: 'italic', fontWeight: 700 }}>AFFIRMATION</span>
              </div>
              <div
                style={{
                  flexShrink: 0,
                  textAlign: 'center',
                  height: 'auto',
                  fontFamily: 'Space Grotesk',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '1.5',
                  display: 'block',
                  textTransform: 'uppercase',
                  color: '#FFFFFF',
                  opacity: 0.4,
                }}
              >
                TAP TO REVEAL
              </div>
            </div>
            {/* Subtitle - at bottom */}
            <div className="absolute bottom-6 left-0 right-0 z-10">
              <span
                style={{
                  fontFamily: 'Space Grotesk',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16.8px',
                  color: '#CCCCCC',
                  textAlign: 'center',
                  opacity: 1,
                  display: 'block',
                }}
              >
                Your affirmation lies beneath
              </span>
            </div>
          </Card>
        </motion.div>
      ) : isGenerating ? (
        <motion.div
          key="generating"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={fillHeight ? 'h-full' : ''}
          style={fillHeight ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}}
        >
          <Card variant="elevated" className={`flex items-center justify-center ${fillHeight ? 'h-full flex-1' : ''}`} style={cardStyle} backgroundImage="/images/affirmation/generating-bg.png">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              {[0, 1, 2].map((index) => (
                <motion.div
                  key={index}
                  className="w-2 h-2 bg-orange-primary rounded-full"
                  animate={{
                    opacity: [0.3, 1, 0.3],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    delay: index * 0.2,
                    ease: 'easeInOut',
                  }}
                />
              ))}
            </div>
            <Text
              variant="body"
              size="sm"
              className="text-white-p60"
            >
              Generating your affirmation...
            </Text>
          </div>
        </Card>
        </motion.div>
      ) : affirmation ? (
        <motion.div
          key="affirmation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={fillHeight ? 'h-full' : ''}
          style={fillHeight ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}}
        >
          <Card 
          variant="elevated" 
          className="flex flex-col h-full relative" 
          style={fillHeight ? { ...cardStyle, height: '100%' } : cardStyle}
          backgroundImage="/images/affirmation/dailyaffirmation.png"
          overlay={
            <div 
              className="absolute inset-0 rounded-2xl pointer-events-none"
              style={{
                background: 'rgba(0, 0, 0, 0.6)',
                zIndex: 4,
              }}
            />
          }
        >
          {/* Reset button - for testing */}
          <button
            onClick={handleReset}
            className="absolute top-6 left-6 z-20 flex items-center justify-center transition-opacity active:opacity-80"
            style={{
              width: '32px',
              height: '32px',
              backgroundColor: 'rgba(39, 39, 39, 0.8)',
              borderRadius: '8px',
            }}
            aria-label="Reset affirmation"
            title="Reset (testing)"
          >
            <ArrowClockwise
              size={16}
              weight="regular"
              style={{
                color: 'rgba(255, 255, 255, 0.6)'
              }}
            />
          </button>
          
          {/* Audio button - only show when revealed */}
          {isRevealed && (
            <>
              {/* Gradient definition for icon */}
              <svg width="0" height="0" className="absolute">
                <defs>
                  <linearGradient id="icon-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FFA147" />
                    <stop offset="100%" stopColor="#FE5706" />
                  </linearGradient>
                </defs>
              </svg>
              <button
                onClick={() => {
                  if (!speechSynthesis) return;
                  
                  if (isSpeaking) {
                    // Stop speaking
                    speechSynthesis.cancel();
                    setIsSpeaking(false);
                  } else {
                    // Start speaking
                    const utterance = new SpeechSynthesisUtterance(affirmation.text);
                    utterance.rate = 0.85; // Slightly slower for calm, meditative feel
                    utterance.pitch = 0.95; // Slightly lower pitch for warmth
                    utterance.volume = 1;
                    
                    // Select a calming, premium voice if available
                    const voices = speechSynthesis.getVoices();
                    // Prefer: Samantha (macOS), Google UK English Female, or any female en-GB/en-US voice
                    const preferredVoice = voices.find(v => 
                      v.name.includes('Samantha') || 
                      v.name.includes('Karen') ||
                      v.name.includes('Google UK English Female') ||
                      v.name.includes('Moira') ||
                      (v.lang.startsWith('en') && v.name.toLowerCase().includes('female'))
                    ) || voices.find(v => v.lang.startsWith('en-GB')) 
                      || voices.find(v => v.lang.startsWith('en'));
                    
                    if (preferredVoice) {
                      utterance.voice = preferredVoice;
                    }
                    
                    utterance.onstart = () => setIsSpeaking(true);
                    utterance.onend = () => setIsSpeaking(false);
                    utterance.onerror = () => setIsSpeaking(false);
                    
                    speechSynthesis.speak(utterance);
                  }
                }}
                className="absolute top-6 right-6 z-20 flex items-center justify-center transition-opacity active:opacity-80"
                aria-label={isSpeaking ? 'Stop reading' : 'Read affirmation'}
              >
                {isSpeaking ? (
                  <SpeakerHigh
                    size={20}
                    weight="fill"
                    style={{
                      color: '#FE5706'
                    }}
                  />
                ) : (
                  <SpeakerX
                    size={20}
                    weight="regular"
                    style={{
                      color: 'rgba(255, 255, 255, 0.4)'
                    }}
                  />
                )}
              </button>
            </>
          )}
          
          <div className="text-center flex-1 flex flex-col justify-center relative z-10">
            <Text
              variant="body"
              size="xl"
              weight="regular"
              className="text-white-pure leading-relaxed"
            >
              {renderAffirmationText(affirmation.text, affirmation.highlightedWords)}
            </Text>
          </div>
          <div className="mt-6 flex justify-center relative z-10 flex-shrink-0">
            <button
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-[15px] border border-white-p90 h-9 transition-all active:bg-white-pure [&:active_span]:!text-black [&:active_span]:!opacity-100 [&:active>div>img]:brightness-0"
              onClick={() => {
                console.log('Button clicked');
              }}
            >
              <span
                className="transition-colors"
                style={{
                  fontFamily: 'Space Grotesk',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '28px',
                  color: '#E6E6E6',
                }}
              >
                Ask and reflect with
              </span>
              <div className="flex items-center gap-1.5">
                <img 
                  src="/svg/godgpticon.svg" 
                  alt="GodGPT Icon" 
                  className="w-[13px] h-4 transition-all group-active:brightness-0"
                />
                <span
                  className="transition-colors"
                  style={{
                    fontFamily: 'Space Grotesk',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '28px',
                    color: '#E6E6E6',
                  }}
                >
                  GodGPT
                </span>
              </div>
            </button>
          </div>
        </Card>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default AffirmationCard;
